import type { DerivedMessageStatus } from '@/bt-common/services/message-status';

/**
 * Pure (React-free) mapping from the canonical two-axis {primary, receipt} to the
 * badge's label + icon + tone. Extracted so it is unit-testable under jest's `node`
 * env without importing lucide/UI. The renderer (MessageLogStatusBadge.tsx) maps
 * `icon`/`tone` to the concrete lucide component + tailwind classes.
 *
 * Label rule: the badge reads as the single most-advanced MILESTONE the message
 * reached — `Read` › `Delivered` › `Sent`, plus `Queued` / `Failed` — matching the
 * campaign detail page's plain Sent / Delivered / Read / Failed vocabulary. The
 * internal wire×receipt distinction (`confirmed` vs `sent`, receipts-not-reported)
 * is NOT surfaced as user-facing jargon: a message WhatsApp's servers acknowledged
 * but that carries no delivery receipt (a group, or a send to yourself) simply reads
 * "Sent" rather than "Confirmed · receipts n/a". A `read`/`delivered` receipt names
 * that milestone directly ("Read" / "Delivered"), regardless of the wire rung.
 */
// Semantic tones — the renderer maps each to the single-source-of-truth palette
// (CAMPAIGN_STATUS_COLORS): sent = dark grey, delivered = green, read = LIGHT green,
// failed = red, queued = slate.
export type BadgeTone = 'queued' | 'failed' | 'read' | 'delivered' | 'sent';
export type BadgeIconName = 'clock' | 'check' | 'checkcheck' | 'xcircle';
export interface BadgeVisual {
  label: string;
  icon: BadgeIconName;
  tone: BadgeTone;
}

export function badgeVisualFromDerived({ primary, receipt }: DerivedMessageStatus): BadgeVisual {
  if (primary === 'failed') return { label: 'Failed', icon: 'xcircle', tone: 'failed' };
  if (primary === 'queued') return { label: 'Queued', icon: 'clock', tone: 'queued' };

  // Reached WhatsApp — name the most-advanced milestone, matching the campaign
  // detail page. Tones map to the shared palette: read = double blue (✓✓),
  // delivered = green (✓✓), sent = grey (single ✓).
  if (receipt === 'read') return { label: 'Read', icon: 'checkcheck', tone: 'read' };
  if (receipt === 'delivered') return { label: 'Delivered', icon: 'checkcheck', tone: 'delivered' };
  // 'not_reported' (group / self-send, no receipt) and 'none' (not yet acked) both
  // read as the plain reached-WhatsApp milestone — no "Confirmed"/"n/a" jargon.
  return { label: 'Sent', icon: 'check', tone: 'sent' };
}
